import {
  AISystemOverviewResponse,
  AISystemsResponse,
  AISystemSummaryDto,
  AiSystemUserQuotaCheck,
  BulkImportResponse,
  BulkImportTemplateFormat,
  CEMark,
  ChangeAssessmentRequest,
  ChangeAssessmentResponse,
  Owner,
  QuestionAnswer,
  StartAiSystemRequest,
  StartAiSystemResponse,
  TransferOwnershipResponse
} from '../types/AISystems.types';
import API from './ApiBase';

type FetchAISystemsParams = {
  offset?: number;
  limit?: number;
  search?: string;
  entity?: string[];
  riskClassification?: string[];
  complianceStatus?: string[];
  ownerId?: string[];
  organization?: string[];
  sortBy?: string;
  order?: 'asc' | 'desc';
};

type FetchAISystemsRequestOptions = {
  suppressForbiddenRedirect?: boolean;
};

export const fetchAISystems = async (
  params: FetchAISystemsParams = {},
  options: FetchAISystemsRequestOptions = {}
): Promise<AISystemsResponse> => {
  try {
    const searchParams = new URLSearchParams();

    if (params.offset !== undefined)
      searchParams.append('offset', params.offset.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('keyword', params.search);

    if (params.entity?.length) {
      params.entity.forEach((value) => searchParams.append('entity', value));
    }
    if (params.riskClassification?.length) {
      params.riskClassification.forEach((value) =>
        searchParams.append('riskClassification', value)
      );
    }
    if (params.complianceStatus?.length) {
      params.complianceStatus.forEach((value) =>
        searchParams.append('complianceStatus', value)
      );
    }
    if (params.ownerId?.length) {
      params.ownerId.forEach((value) => searchParams.append('ownerId', value));
    }
    if (params.organization?.length) {
      params.organization.forEach((value) =>
        searchParams.append('organization', value)
      );
    }
    if (params.sortBy) {
      searchParams.append('sortBy', params.sortBy);
    }
    if (params.order) {
      searchParams.append('order', params.order);
    }

    const queryString = searchParams.toString();
    const url = `/ai-systems${queryString ? `?${queryString}` : ''}`;

    const response = await API.get(
      url,
      options.suppressForbiddenRedirect
        ? ({ skipAccessDeniedRedirect: true } as any)
        : undefined
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch AI systems'
    );
  }
};

export const startAiSystem = async (requestBody: StartAiSystemRequest) => {
  try {
    const response = await API.post<StartAiSystemResponse>(
      '/ai-systems/prestep/start',
      requestBody
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const deleteAiSystem = async (aiSystemId: string) => {
  try {
    const response = await API.delete(`/ai-systems/${aiSystemId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to delete creating AI system'
    );
  }
};

export const getAiSystemsSummary = async () => {
  try {
    const response = await API.get('/ai-systems/summary');
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch AI systems summary'
    );
  }
};

export const fetchOrganizations = async (): Promise<string[]> => {
  try {
    const response = await API.get('/ai-systems/organizations');
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch organizations'
    );
  }
};

// queue answer requests so they run one at a time
// return response data only for last request
let lastAnswerRequestId = 0;
let answerQuestionQueue: Promise<void> = Promise.resolve();
export const answerQuestion = async (
  questionnaireId: string,
  question: QuestionAnswer
) => {
  const requestId = ++lastAnswerRequestId;

  try {
    const request = answerQuestionQueue.then(async () => {
      const response = await API.post(
        `/questionnaire/${questionnaireId}/answer`,
        question
      );

      // ignore if the response arrived after a more recent request
      if (requestId < lastAnswerRequestId) return;

      return response.data;
    });

    answerQuestionQueue = request.then(() => undefined).catch(() => undefined);

    return await request;
  } catch (error: any) {
    throw error;
  }
};

export const questionnaireStepBack = async (
  questionnaireId: string,
  stepId: string
) => {
  try {
    const response = await API.post(
      `/questionnaire/${questionnaireId}/${stepId}/back`
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const getQuestionnaireDefinition = async (questionnaireId: string) => {
  try {
    const response = await API.get(
      `/questionnaire/questionnaire-versions/${questionnaireId}/definition`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        'Failed to fetch questionnaire definitions'
    );
  }
};

export const updateAiSystem = async (aiSystemId: string): Promise<void> => {
  try {
    await API.put(`/ai-systems/${aiSystemId}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to update AI system'
    );
  }
};

export const fetchAISystemsSummary = async (): Promise<AISystemSummaryDto> => {
  try {
    const response = await API.get('/ai-systems/summary');
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch AI systems summary'
    );
  }
};

export const fetchOwners = async (): Promise<Owner[]> => {
  try {
    const response = await API.get('/ai-systems/owners');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch owners');
  }
};

export const transferOwnership = async (
  aiSystemId: string,
  newOwnerId: string
): Promise<TransferOwnershipResponse> => {
  try {
    const response = await API.patch<TransferOwnershipResponse>(
      `/ai-systems/${aiSystemId}/transfer`,
      null,
      {
        params: { newOwnerId }
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to transfer AI system ownership'
    );
  }
};

export const getAISystemOverview = async (
  aiSystemId: string
): Promise<AISystemOverviewResponse> => {
  try {
    const response = await API.get(`/ai-systems/${aiSystemId}/overview`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch AI system overview'
    );
  }
};

export const checkAiSystemUserQuota = async (
  aiSystemId: string,
  email?: string
): Promise<AiSystemUserQuotaCheck> => {
  try {
    const searchParams = new URLSearchParams();
    if (email) {
      searchParams.append('email', email);
    }

    const queryString = searchParams.toString();
    const url = `/ai-systems/${aiSystemId}/quota/users/check${
      queryString ? `?${queryString}` : ''
    }`;
    const response = await API.get(url);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch AI system user quota'
    );
  }
};

export const generateQuestionnaireReportPdf = async (
  aiSystemId: string
): Promise<{ message: string; s3Path: string }> => {
  try {
    const response = await API.post(
      `/questionnaire/ai-systems/${aiSystemId}/report/generate-pdf`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        'Failed to generate questionnaire PDF report'
    );
  }
};

export const continueAiSystemDraft = async (aiSystemId: string) => {
  try {
    const response = await API.get(`/questionnaire/ai-systems/${aiSystemId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to fetch ai system'
    );
  }
};

export const getCEMark = async (aiSystemId: string): Promise<CEMark | null> => {
  try {
    const response = await API.get(`/ce-mark/${aiSystemId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch CE mark');
  }
};

export const changeAssessment = async (
  aiSystemId: string,
  requestBody: ChangeAssessmentRequest
) => {
  try {
    const response = await API.post<ChangeAssessmentResponse>(
      `/ai-systems/${aiSystemId}/change-assessment`,
      requestBody
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to change assessment'
    );
  }
};

export const downloadBulkImportTemplate = async (
  format: BulkImportTemplateFormat
): Promise<Blob> => {
  try {
    const response = await API.get<Blob>('/imports/template', {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to download bulk import template'
    );
  }
};

export const bulkImportAiSystems = async (
  file: File
): Promise<BulkImportResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await API.post<BulkImportResponse>(
      '/imports/upload',
      formData
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || 'Failed to import AI systems'
    );
  }
};

export const downloadBulkImportFailureReport = async (
  reportId: string
): Promise<Blob> => {
  try {
    const response = await API.get<Blob>(
      `/imports/failure-report/${reportId}`,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        'Failed to download bulk import failure report'
    );
  }
};
